import { useEffect, useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, Toggle, useLoad, when } from './kit';

type AuthType = 'none' | 'api_key' | 'basic' | 'bearer' | 'oauth2';
interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  auth_type: AuthType;
  template: string | null;
  signing_secret: string;
  active: boolean;
  delivered_today: number;
  failed_today: number;
  waiting: number;
  last_status: 'delivered' | 'failed' | 'pending' | null;
}
interface Delivery {
  id: string;
  event: string;
  status: 'pending' | 'delivered' | 'failed';
  attempts: number;
  last_status: number | null;
  last_error: string | null;
  created_at: string;
  delivered_at: string | null;
  next_attempt_at: string | null;
  payload: unknown;
}
interface Lookup {
  url: string;
  auth_type: AuthType;
  name_path: string | null;
  record_url_path: string | null;
  fields: { label: string; path: string }[];
}

const EVENTS: { id: string; label: string }[] = [
  { id: 'interaction.dispositioned', label: 'Call closed with an outcome (CRM write-back)' },
  { id: 'call.answered', label: 'Call answered' },
  { id: 'call.ended', label: 'Call ended' },
  { id: 'call.abandoned', label: 'Caller hung up before an answer' },
  { id: 'call.queued', label: 'Caller waiting in a queue' },
  { id: 'call.transferred', label: 'Call transferred' },
  { id: 'agent.state_changed', label: 'Agent status changed' },
];
const AUTH: { id: AuthType; label: string }[] = [
  { id: 'none', label: 'None' },
  { id: 'api_key', label: 'API key in a header' },
  { id: 'bearer', label: 'Bearer token' },
  { id: 'basic', label: 'Username and password (basic)' },
  { id: 'oauth2', label: 'OAuth 2.0 (client credentials)' },
];
const CRM_TEMPLATE = JSON.stringify(
  {
    phone: '{{interaction.customer_number}}',
    subject: 'Call ({{interaction.direction}}): {{interaction.disposition_label}}',
    outcome: '{{interaction.disposition_code}}',
    notes: '{{interaction.notes}}',
    agent_email: '{{interaction.agent_email}}',
    talk_seconds: '{{interaction.talk_seconds}}',
    started_at: '{{interaction.started_at}}',
    nova_interaction_id: '{{interaction.id}}',
  },
  null,
  2,
);

/** Credentials for one auth type. On an existing item, untouched fields keep what is stored. */
function useAuth(saved: AuthType | undefined) {
  const [type, setType] = useState<AuthType>(saved ?? 'none');
  const [v, setV] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(!saved);
  const set = (k: string, value: string) => {
    setV({ ...v, [k]: value });
    setTouched(true);
  };
  const body = (): Record<string, string> | undefined => {
    if (!touched) return undefined;
    switch (type) {
      case 'api_key':
        return { type, header: v.header || 'X-Api-Key', value: v.value ?? '' };
      case 'basic':
        return { type, username: v.username ?? '', password: v.password ?? '' };
      case 'bearer':
        return { type, token: v.token ?? '' };
      case 'oauth2':
        return { type, tokenUrl: v.tokenUrl ?? '', clientId: v.clientId ?? '', clientSecret: v.clientSecret ?? '', ...(v.scope ? { scope: v.scope } : {}) };
      default:
        return { type: 'none' };
    }
  };
  const fields = (
    <>
      <Field label="Authentication">
        {(id) => (
          <select
            id={id}
            className={inputClass}
            value={type}
            onChange={(e) => {
              setType(e.target.value as AuthType);
              setTouched(true);
            }}
          >
            {AUTH.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        )}
      </Field>
      {saved && saved !== 'none' && !touched ? <p className="-mt-2 mb-4 text-xs text-ink-3">Saved credentials are kept. Type new ones to replace them.</p> : null}
      {type === 'api_key' ? (
        <div className="grid grid-cols-[140px_1fr] gap-3">
          <Field label="Header">{(id) => <input id={id} className={inputClass} placeholder="X-Api-Key" value={v.header ?? ''} onChange={(e) => set('header', e.target.value)} />}</Field>
          <Field label="Key">{(id) => <input id={id} type="password" autoComplete="off" className={inputClass} value={v.value ?? ''} onChange={(e) => set('value', e.target.value)} />}</Field>
        </div>
      ) : null}
      {type === 'bearer' ? (
        <Field label="Token">{(id) => <input id={id} type="password" autoComplete="off" className={inputClass} value={v.token ?? ''} onChange={(e) => set('token', e.target.value)} />}</Field>
      ) : null}
      {type === 'basic' ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Username">{(id) => <input id={id} autoComplete="off" className={inputClass} value={v.username ?? ''} onChange={(e) => set('username', e.target.value)} />}</Field>
          <Field label="Password">{(id) => <input id={id} type="password" autoComplete="new-password" className={inputClass} value={v.password ?? ''} onChange={(e) => set('password', e.target.value)} />}</Field>
        </div>
      ) : null}
      {type === 'oauth2' ? (
        <>
          <Field label="Token address">{(id) => <input id={id} className={inputClass} placeholder="https://login.example.com/oauth/token" value={v.tokenUrl ?? ''} onChange={(e) => set('tokenUrl', e.target.value.trim())} />}</Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client ID">{(id) => <input id={id} autoComplete="off" className={inputClass} value={v.clientId ?? ''} onChange={(e) => set('clientId', e.target.value)} />}</Field>
            <Field label="Client secret">{(id) => <input id={id} type="password" autoComplete="off" className={inputClass} value={v.clientSecret ?? ''} onChange={(e) => set('clientSecret', e.target.value)} />}</Field>
          </div>
          <Field label="Scope" hint="Optional.">{(id) => <input id={id} className={inputClass} value={v.scope ?? ''} onChange={(e) => set('scope', e.target.value)} />}</Field>
        </>
      ) : null}
    </>
  );
  return { body, fields };
}

/** Admin: the CRM lookup for screen pops, and webhooks (CRM write-back and other events). */
export function Integrations() {
  return (
    <>
      <CrmLookup />
      <div className="h-10" />
      <Webhooks />
    </>
  );
}

// ------------------------------------------------------------------ CRM lookup
function CrmLookup() {
  const [loaded, reload] = useLoad<{ lookup: Lookup | null }>('/crm-lookup');
  const saved = loaded?.lookup;
  const [editing, setEditing] = useState(false);
  return (
    <>
      <SectionHeader
        title="CRM lookup"
        lead="When a call rings, Nova asks your CRM about the caller and shows the answer in the agent's Customer panel."
        action={
          <Button tone={saved ? 'quiet' : 'primary'} className="px-3.5 py-2" onClick={() => setEditing(true)}>
            {saved ? 'Change' : 'Set up'}
          </Button>
        }
      />
      <div className="rounded-xl border border-line bg-hull/60 p-5 text-sm">
        {!saved ? (
          <p className="text-ink-2">Not set up. Agents still see Nova's own customer details and history.</p>
        ) : (
          <dl className="grid gap-2 sm:grid-cols-[160px_1fr]">
            <dt className="text-ink-3">Address</dt>
            <dd className="break-all font-mono text-xs">{saved.url}</dd>
            <dt className="text-ink-3">Authentication</dt>
            <dd>{AUTH.find((a) => a.id === saved.auth_type)?.label}</dd>
            <dt className="text-ink-3">Shown to agents</dt>
            <dd>{[saved.name_path ? 'name' : null, ...saved.fields.map((f) => f.label), saved.record_url_path ? 'link to the record' : null].filter(Boolean).join(', ') || 'Nothing chosen yet'}</dd>
          </dl>
        )}
      </div>
      {editing ? (
        <LookupDrawer
          saved={saved ?? null}
          onClose={() => setEditing(false)}
          onDone={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function LookupDrawer({ saved, onClose, onDone }: { saved: Lookup | null; onClose: () => void; onDone: () => void }) {
  const [url, setUrl] = useState(saved?.url ?? '');
  const [namePath, setNamePath] = useState(saved?.name_path ?? '');
  const [recordPath, setRecordPath] = useState(saved?.record_url_path ?? '');
  const [fields, setFields] = useState(saved?.fields.length ? saved.fields : [{ label: '', path: '' }]);
  const [number, setNumber] = useState('');
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const auth = useAuth(saved?.auth_type);

  const body = () => ({
    url: url.trim(),
    auth: auth.body(),
    namePath: namePath.trim() || null,
    recordUrlPath: recordPath.trim() || null,
    fields: fields.filter((f) => f.label.trim() && f.path.trim()).map((f) => ({ label: f.label.trim(), path: f.path.trim() })),
  });
  async function save(close: boolean) {
    setBusy(true);
    const ok = await act(() => api('PUT', '/crm-lookup', body()).then(() => true), close ? 'CRM lookup saved' : undefined);
    setBusy(false);
    if (ok && close) onDone();
    return ok;
  }
  async function test() {
    if (!(await save(false))) return;
    setResult(null);
    const r = await act(() => api<Record<string, unknown>>('POST', '/crm-lookup/test', { number: number.replace(/[^\d+]/g, '') }));
    if (r) setResult(r);
  }

  return (
    <Drawer
      title="CRM lookup"
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" disabled={busy || !url.includes('{caller_number}')} onClick={() => void save(true)}>
            Save
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          {saved ? (
            <span className="ml-auto">
              <ConfirmButton
                confirmText="Remove lookup"
                onConfirm={async () => {
                  if (await act(() => api('DELETE', '/crm-lookup').then(() => true), 'CRM lookup removed')) onDone();
                }}
              >
                Remove
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      <Field label="Address" hint="Nova calls it with GET and fills in {caller_number}. The CRM answers with JSON; a list means matching records and the first is used.">
        {(id) => <input id={id} className={`${inputClass} font-mono text-xs`} placeholder="https://crm.example.com/api/contacts?phone={caller_number}" value={url} onChange={(e) => setUrl(e.target.value.trim())} />}
      </Field>
      {auth.fields}
      <p className="mb-2 mt-2 text-sm font-medium">What agents see</p>
      <p className="mb-3 text-xs text-ink-3">Fields of the CRM's reply, such as name, account.status or owner.email.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name field">{(id) => <input id={id} className={`${inputClass} font-mono text-xs`} placeholder="name" value={namePath} onChange={(e) => setNamePath(e.target.value.trim())} />}</Field>
        <Field label="Link to the record">{(id) => <input id={id} className={`${inputClass} font-mono text-xs`} placeholder="url" value={recordPath} onChange={(e) => setRecordPath(e.target.value.trim())} />}</Field>
      </div>
      {fields.map((f, i) => (
        <div key={i} className="mb-2 grid grid-cols-[1fr_1fr_auto] gap-2">
          <input aria-label={`Label ${i + 1}`} className={inputClass} placeholder="Label, e.g. Plan" value={f.label} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
          <input aria-label={`Field ${i + 1}`} className={`${inputClass} font-mono text-xs`} placeholder="account.plan" value={f.path} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, path: e.target.value.trim() } : x)))} />
          <Button tone="ghost" className="px-2" aria-label="Remove field" onClick={() => setFields(fields.filter((_, j) => j !== i))}>
            ×
          </Button>
        </div>
      ))}
      {fields.length < 8 ? (
        <Button tone="ghost" className="mb-5 px-2 py-1 text-xs" onClick={() => setFields([...fields, { label: '', path: '' }])}>
          + Add a field
        </Button>
      ) : null}

      <div className="rounded-xl border border-line p-4">
        <p className="mb-2 text-sm font-medium">Try it</p>
        <div className="flex gap-2">
          <input aria-label="Test number" inputMode="tel" className={`${inputClass} tabular`} placeholder="254711222333" value={number} onChange={(e) => setNumber(e.target.value)} />
          <Button tone="quiet" disabled={busy || number.replace(/\D/g, '').length < 3 || !url.includes('{caller_number}')} onClick={() => void test()}>
            Test
          </Button>
        </div>
        {result ? (
          <div className="mt-3 text-sm" aria-live="polite">
            <p className={result.error ? 'text-break' : 'text-ink'}>
              {result.error ? String(result.error) : result.found ? `Found: ${String(result.name ?? 'a record')}` : 'No record for this number'}
            </p>
            {Array.isArray(result.fields) && result.fields.length ? (
              <ul className="mt-1 text-ink-2">
                {(result.fields as { label: string; value: string }[]).map((f) => (
                  <li key={f.label}>
                    {f.label}: {f.value}
                  </li>
                ))}
              </ul>
            ) : null}
            {result.raw ? <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-abyss/60 p-2 text-xs">{typeof result.raw === 'string' ? result.raw : JSON.stringify(result.raw, null, 2)}</pre> : null}
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}

// ------------------------------------------------------------------ webhooks
function Webhooks() {
  const [hooks, reload] = useLoad<Webhook[]>('/webhooks');
  const [open, setOpen] = useState<Webhook | 'new' | null>(null);
  const [log, setLog] = useState<Webhook | null>(null);
  return (
    <>
      <SectionHeader
        title="Webhooks and CRM write-back"
        lead='Nova posts events to your systems, signed and retried. For CRM write-back, send "Call closed with an outcome" to your CRM with a field mapping.'
        action={<AddButton onClick={() => setOpen('new')}>Add webhook</AddButton>}
      />
      <Table head={['Name', 'Address', 'Events', 'Last 24 hours', '']} empty={hooks?.length === 0 && 'No webhooks yet.'}>
        {(hooks ?? []).map((h) => (
          <Row key={h.id} onClick={() => setOpen(h)} muted={!h.active}>
            <Cell className="font-medium">
              {h.name}
              {!h.active ? <span className="ml-2 text-xs text-ink-3">off</span> : null}
            </Cell>
            <Cell className="max-w-[260px] truncate font-mono text-xs">{h.url}</Cell>
            <Cell>{h.events.map((e) => EVENTS.find((x) => x.id === e)?.label.replace(' (CRM write-back)', '') ?? e).join(', ')}</Cell>
            <Cell>
              <span className="flex flex-wrap gap-1">
                <Pill tone="good">{h.delivered_today} sent</Pill>
                {h.failed_today ? <Pill tone="bad">{h.failed_today} failed</Pill> : null}
                {h.waiting ? <Pill tone="warn">{h.waiting} retrying</Pill> : null}
              </span>
            </Cell>
            <Cell className="text-right">
              <span onClick={(e) => e.stopPropagation()}>
                <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => setLog(h)}>
                  Delivery log
                </Button>
              </span>
            </Cell>
          </Row>
        ))}
      </Table>
      {open ? (
        <WebhookDrawer
          hook={open === 'new' ? null : open}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            reload();
          }}
          onLog={(h) => {
            setOpen(null);
            setLog(h);
          }}
        />
      ) : null}
      {log ? <DeliveryLog hook={log} onClose={() => (setLog(null), reload())} /> : null}
    </>
  );
}

function WebhookDrawer({ hook, onClose, onDone, onLog }: { hook: Webhook | null; onClose: () => void; onDone: () => void; onLog: (h: Webhook) => void }) {
  const [name, setName] = useState(hook?.name ?? '');
  const [url, setUrl] = useState(hook?.url ?? '');
  const [events, setEvents] = useState<string[]>(hook?.events ?? ['interaction.dispositioned']);
  const [template, setTemplate] = useState(hook?.template ?? '');
  const [active, setActive] = useState(hook?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ id: string; signing_secret: string } | null>(null);
  const [tested, setTested] = useState<{ ok: boolean; status: number; error: string | null } | null>(null);
  const auth = useAuth(hook?.auth_type);
  const id = hook?.id ?? created?.id;

  let templateError: string | null = null;
  if (template.trim()) {
    try {
      JSON.parse(template);
    } catch (err) {
      templateError = (err as Error).message;
    }
  }

  async function save(): Promise<string | null> {
    setBusy(true);
    const body = { name: name.trim(), url: url.trim(), events, auth: auth.body(), template: template.trim() || null, active };
    const r = id
      ? await act(() => api('PATCH', `/webhooks/${id}`, body).then(() => ({ id: id! })))
      : await act(() => api<{ id: string; signing_secret: string }>('POST', '/webhooks', body));
    setBusy(false);
    if (r && !id) setCreated(r as { id: string; signing_secret: string });
    return r ? (r as { id: string }).id : null;
  }
  async function test() {
    const saved = await save();
    if (!saved) return;
    setTested(null);
    const r = await act(() => api<{ ok: boolean; status: number; error: string | null }>('POST', `/webhooks/${saved}/test`));
    if (r) setTested(r);
  }

  const valid = name.trim().length >= 2 && /^https?:\/\//.test(url.trim()) && events.length > 0 && !templateError;
  const secret = hook?.signing_secret ?? created?.signing_secret;

  return (
    <Drawer
      title={hook ? hook.name : created ? name : 'Add a webhook'}
      open
      onClose={created ? onDone : onClose}
      footer={
        <>
          <Button
            tone="primary"
            disabled={busy || !valid}
            onClick={async () => {
              if (await save()) onDone();
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button tone="quiet" disabled={busy || !valid} onClick={() => void test()}>
            Save and send a test
          </Button>
          {hook ? (
            <span className="ml-auto flex gap-1">
              <Button tone="ghost" className="px-3 py-2" onClick={() => onLog(hook)}>
                Delivery log
              </Button>
              <ConfirmButton
                confirmText="Delete webhook"
                onConfirm={async () => {
                  if (await act(() => api('DELETE', `/webhooks/${hook.id}`).then(() => true), `${hook.name} deleted`)) onDone();
                }}
              >
                Delete
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      {tested ? (
        <p className={`mb-4 rounded-lg border px-3 py-2 text-sm ${tested.ok ? 'border-available/40 text-available' : 'border-break/40 text-break'}`} aria-live="polite">
          {tested.ok ? `Test delivered (HTTP ${tested.status}).` : `Test failed: ${tested.error}`}
        </p>
      ) : null}
      <Field label="Name" hint="For example: HubSpot write-back, n8n.">
        {(fid) => <input id={fid} className={inputClass} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label="Address" hint="Nova sends a POST with a JSON body.">
        {(fid) => <input id={fid} className={`${inputClass} font-mono text-xs`} placeholder="https://crm.example.com/api/calls" value={url} onChange={(e) => setUrl(e.target.value.trim())} />}
      </Field>
      <p className="mb-1.5 text-sm font-medium">Send these events</p>
      <div className="mb-4 flex flex-col gap-1.5">
        {EVENTS.map((e) => (
          <label key={e.id} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={events.includes(e.id)} onChange={(x) => setEvents(x.target.checked ? [...events, e.id] : events.filter((v) => v !== e.id))} />
            {e.label}
          </label>
        ))}
      </div>
      {auth.fields}
      <Field
        label="Field mapping"
        hint='Optional. A JSON body where {{interaction.customer_number}}, {{interaction.disposition_label}}, {{interaction.notes}}, {{event}} and other fields of the standard payload are filled in. Empty sends the standard payload.'
      >
        {(fid) => (
          <textarea
            id={fid}
            rows={9}
            spellCheck={false}
            className={`${inputClass} font-mono text-xs`}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          />
        )}
      </Field>
      {templateError ? <p className="-mt-2 mb-3 text-xs text-break">Not valid JSON: {templateError}</p> : null}
      {!template.trim() ? (
        <Button tone="ghost" className="-mt-2 mb-4 px-2 py-1 text-xs" onClick={() => setTemplate(CRM_TEMPLATE)}>
          Start from a CRM write-back example
        </Button>
      ) : null}
      <Toggle label="Switched on" checked={active} onChange={setActive} />
      {secret ? (
        <div className="rounded-xl border border-line p-3 text-xs">
          <p className="mb-1 font-medium">Signing secret</p>
          <code className="block break-all">{secret}</code>
          <p className="mt-2 text-ink-3">
            Each request has X-Nova-Signature: t=time,v1=HMAC-SHA256 of "time.body" with this secret. Check it to know a request came from Nova.
          </p>
        </div>
      ) : null}
    </Drawer>
  );
}

function DeliveryLog({ hook, onClose }: { hook: Webhook; onClose: () => void }) {
  const [rows, reload] = useLoad<Delivery[]>(`/webhooks/${hook.id}/deliveries`);
  const [shown, setShown] = useState<string | null>(null);
  useEffect(() => {
    const t = window.setInterval(reload, 5000);
    return () => window.clearInterval(t);
  }, [reload]);
  return (
    <Drawer title={`${hook.name}: deliveries`} open onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
      {rows?.length === 0 ? <p className="text-sm text-ink-2">Nothing sent yet.</p> : null}
      <ul className="flex flex-col gap-2">
        {(rows ?? []).map((d) => (
          <li key={d.id} className="rounded-xl border border-line p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone={d.status === 'delivered' ? 'good' : d.status === 'failed' ? 'bad' : 'warn'}>
                {d.status === 'delivered' ? 'Delivered' : d.status === 'failed' ? 'Failed' : 'Retrying'}
              </Pill>
              <span className="font-medium">{d.event === 'webhook.test' ? 'Test' : EVENTS.find((e) => e.id === d.event)?.label ?? d.event}</span>
              <span className="ml-auto text-xs text-ink-3">{when(d.created_at)}</span>
            </div>
            <p className="mt-1 text-xs text-ink-2">
              {d.attempts} attempt{d.attempts === 1 ? '' : 's'}
              {d.last_status ? `, last answer HTTP ${d.last_status}` : ''}
              {d.next_attempt_at ? `, next try ${when(d.next_attempt_at)}` : ''}
            </p>
            {d.last_error && d.status !== 'delivered' ? <p className="mt-1 break-words text-xs text-break">{d.last_error}</p> : null}
            <div className="mt-2 flex gap-1">
              <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => setShown(shown === d.id ? null : d.id)}>
                {shown === d.id ? 'Hide payload' : 'Payload'}
              </Button>
              {d.status !== 'delivered' && d.event !== 'webhook.test' ? (
                <Button
                  tone="quiet"
                  className="px-2 py-1 text-xs"
                  onClick={async () => {
                    if (await act(() => api('POST', `/webhook-deliveries/${d.id}/retry`).then(() => true), 'Sending again')) setTimeout(reload, 1500);
                  }}
                >
                  Send again
                </Button>
              ) : null}
            </div>
            {shown === d.id ? <pre className="mt-2 max-h-60 overflow-auto rounded-lg bg-abyss/60 p-2 text-xs">{JSON.stringify(d.payload, null, 2)}</pre> : null}
          </li>
        ))}
      </ul>
    </Drawer>
  );
}
